import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Bell,
  Bot,
  CheckCircle2,
  Circle,
  Compass,
  LogIn,
  Loader2,
  Rocket,
  Sparkles,
  Store,
  Users,
} from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

import { getCurrentUser } from "@/services/auth";
import { getProvisionStatus, provisionAgents } from "@/services/atelier";
import { generateAppSdd, type CodeGeneration } from "@/services/debugpavilion";

/**
 * 「快速开始」引导页（/guide）：
 * 4 步引导（① Facet 登录 ② 同步内置 Agent ③ 生成应用 ④ 发布坊市），
 * 每步带状态提示；底部展示 docs/使用案例.md 的 3 个真实案例要点。
 * 服务调用可整体 mock（vitest + @testing-library），见 guide.test.tsx。
 */

interface StepStatus {
  done: boolean;
  label: string;
}

function StepStatusBadge({ status }: { status: StepStatus }) {
  if (!status.label) return null;
  return (
    <Badge variant={status.done ? "secondary" : "outline"} className={status.done ? "bg-green-600 text-white hover:bg-green-600" : ""}>
      {status.done ? <CheckCircle2 className="mr-1 h-3 w-3" /> : <Circle className="mr-1 h-3 w-3" />}
      {status.label}
    </Badge>
  );
}

function StepCard({
  step,
  title,
  description,
  status,
  children,
}: {
  step: string;
  title: string;
  description: string;
  status: StepStatus;
  children: React.ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-start gap-4 pb-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-bold text-white shadow-md shadow-indigo-500/20">
          {step}
        </div>
        <div className="flex-1">
          <CardTitle className="flex flex-wrap items-center gap-2 text-lg">{title}</CardTitle>
          <CardDescription className="mt-1">{description}</CardDescription>
        </div>
        <StepStatusBadge status={status} />
      </CardHeader>
      <CardContent className="space-y-4 pt-0">{children}</CardContent>
    </Card>
  );
}

/** 4 步引导的固定流程说明（编号 ①②③④）。 */
const STEPS = [
  {
    step: "①",
    title: "登录（Facet）",
    description: "使用 Facet 统一认证登录：真实 SSO / OIDC，或本地测试账号 admin@example.local / testpass123（role=user）。登录后顶部栏出现通知铃铛。",
  },
  {
    step: "②",
    title: "同步内置 Agent",
    description: "把 7 个内置代码生成 Agent（SDD 架构师 / 代码工程师 / 代码审查员 / 测试工程师 / 部署工程师 / 开发助手 / 代码探索者）幂等构建到 Atelier。",
  },
  {
    step: "③",
    title: "生成应用",
    description: "输入自然语言需求，触发 SDD 5 阶段流水线（recon → foundation → specs → parallel_build → assembly_qa），自动生成页面、数据模型与代码产物。",
  },
  {
    step: "④",
    title: "发布坊市",
    description: "在「应用管理」点「发布到坊市」，经 Decross 审核（draft → published / rejected）后对外分发，支持 iframe 免登预览。",
  },
];

export function GuidePage() {
  const navigate = useNavigate();

  // ① 登录状态（真实校验当前用户会话）
  const [user, setUser] = React.useState<{ id: string; name: string; email: string } | null>(null);
  const [userLoading, setUserLoading] = React.useState(true);

  // ② 内置 Agent 同步
  const [provisioning, setProvisioning] = React.useState(false);
  const [provisionResult, setProvisionResult] = React.useState<{
    created: number;
    reused: number;
    failed: number;
    detail?: string;
  } | null>(null);
  const [provisionError, setProvisionError] = React.useState<string | null>(null);

  // ③ 生成应用
  const [requirement, setRequirement] = React.useState("");
  const [generating, setGenerating] = React.useState(false);
  const [generation, setGeneration] = React.useState<CodeGeneration | null>(null);
  const [generationError, setGenerationError] = React.useState<string | null>(null);

  // 进入页面：确认登录态 + 拉取最近一次 provision 结果（失败静默按未同步处理）
  React.useEffect(() => {
    let cancelled = false;
    void getCurrentUser()
      .then((u) => {
        if (cancelled) return;
        setUser({ id: u.id, name: u.name, email: u.email });
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
      })
      .finally(() => {
        if (!cancelled) setUserLoading(false);
      });

    void getProvisionStatus()
      .then((s) => {
        if (cancelled || !s.provisioned) return;
        setProvisionResult({
          created: s.created?.length ?? 0,
          reused: s.reused?.length ?? 0,
          failed: s.failed?.length ?? 0,
          detail: s.detail,
        });
      })
      .catch(() => {
        // 未配置 ATELIER_ADMIN_TOKEN 等情况下忽略，保持「未同步」初始态
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleProvision = async () => {
    setProvisioning(true);
    setProvisionError(null);
    try {
      const result = await provisionAgents();
      setProvisionResult({
        created: result.created.length,
        reused: result.reused.length,
        failed: result.failed.length,
        detail: result.total > 0 ? `共 ${result.total} 个内置 Agent` : undefined,
      });
    } catch (err) {
      setProvisionError(`同步失败：${String(err)}`);
    } finally {
      setProvisioning(false);
    }
  };

  const handleGenerate = async () => {
    const req = requirement.trim();
    if (!req) return;
    setGenerating(true);
    setGeneration(null);
    setGenerationError(null);
    try {
      // 同步模式：等待 5 阶段流水线完成后返回最终记录
      const record = await generateAppSdd({ requirement: req, sync: true });
      setGeneration(record);
    } catch (err) {
      setGenerationError(`生成失败：${String(err)}`);
    } finally {
      setGenerating(false);
    }
  };

  const loginStatus: StepStatus = userLoading
    ? { done: false, label: "检查登录态…" }
    : user
      ? { done: true, label: "已登录" }
      : { done: false, label: "未登录" };

  const provisionStatus: StepStatus = provisioning
    ? { done: false, label: "同步中…" }
    : provisionError
      ? { done: false, label: "同步失败" }
      : provisionResult
        ? {
            done: provisionResult.failed === 0,
            label: provisionResult.failed === 0 ? `已同步（新建 ${provisionResult.created} / 复用 ${provisionResult.reused}）` : `同步异常（失败 ${provisionResult.failed}）`,
          }
        : { done: false, label: "未同步" };

  const generateStatus: StepStatus = generating
    ? { done: false, label: "生成中…" }
    : generationError
      ? { done: false, label: "生成失败" }
      : generation
        ? generation.status === "completed"
          ? { done: true, label: "生成完成" }
          : { done: false, label: "生成失败" }
        : { done: false, label: "未开始" };

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      {/* 页头 */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-indigo-500 to-violet-600 p-8 text-white shadow-lg">
        <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <Compass className="h-6 w-6" />
              快速开始
            </h1>
            <p className="mt-2 max-w-2xl text-indigo-100">
              4 步从登录到发布：登录 Facet → 同步内置 Agent → 生成应用 → 发布到坊市。每步都带状态提示，照做即可。
            </p>
          </div>
          <Badge className="self-start bg-white/20 text-white hover:bg-white/20">
            5 分钟上手
          </Badge>
        </div>
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
      </div>

      {/* 4 步引导 */}
      <div className="space-y-6">
        {/* ① 登录 */}
        <StepCard step={STEPS[0].step} title={STEPS[0].title} description={STEPS[0].description} status={loginStatus}>
          {user ? (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-3 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="font-medium">{user.name}</span>
              <span className="text-muted-foreground">{user.email}</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
              <LogIn className="h-4 w-4" />
              尚未登录，请先登录后再进行后续步骤。
              <Button size="sm" asChild>
                <Link to="/login">去登录</Link>
              </Button>
            </div>
          )}
        </StepCard>

        {/* ② 同步内置 Agent */}
        <StepCard step={STEPS[1].step} title={STEPS[1].title} description={STEPS[1].description} status={provisionStatus}>
          <div className="space-y-3">
            <Button onClick={() => void handleProvision()} disabled={provisioning} data-testid="guide-provision">
              {provisioning ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Bot className="mr-1.5 h-4 w-4" />}
              {provisioning ? "同步中…" : "同步内置 Agent"}
            </Button>
            {provisionResult && !provisionError && (
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded bg-emerald-500/15 px-2 py-0.5" data-testid="guide-provision-created">
                  新建 {provisionResult.created}
                </span>
                <span className="rounded bg-sky-500/15 px-2 py-0.5" data-testid="guide-provision-reused">
                  复用 {provisionResult.reused}
                </span>
                <span className="rounded bg-destructive/15 px-2 py-0.5" data-testid="guide-provision-failed">
                  失败 {provisionResult.failed}
                </span>
                {provisionResult.detail && <span className="self-center text-muted-foreground">{provisionResult.detail}</span>}
              </div>
            )}
            {provisionError && <p className="text-sm text-destructive">{provisionError}</p>}
            <p className="text-xs text-muted-foreground">
              等价于调用 <code className="rounded bg-muted px-1">POST /api/v1/atelier/provision</code>（幂等，需配置 ATELIER_ADMIN_TOKEN）。
            </p>
          </div>
        </StepCard>

        {/* ③ 生成应用 */}
        <StepCard step={STEPS[2].step} title={STEPS[2].title} description={STEPS[2].description} status={generateStatus}>
          <div className="space-y-3">
            <Textarea
              placeholder="例如：创建一个待办事项应用，包含任务列表、新增任务表单、完成状态切换、按状态筛选。"
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              rows={3}
              data-testid="guide-requirement"
            />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void handleGenerate()} disabled={generating || !requirement.trim()} data-testid="guide-generate">
                {generating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                {generating ? "生成中（5 阶段流水线运行中…）" : "直接生成"}
              </Button>
              <Button variant="outline" onClick={() => navigate("/builder")} data-testid="guide-to-builder">
                <ArrowRight className="mr-1.5 h-4 w-4" />
                去设计器生成
              </Button>
            </div>
            {generation && generation.status === "completed" && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-green-600/30 bg-green-600/5 p-3 text-sm" data-testid="guide-generation-result">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>
                  生成完成：应用 <span className="font-medium">{generation.app_id}</span>（{generation.pipeline_stage ?? "已产出"}）
                </span>
                <Button size="sm" asChild className="ml-auto">
                  <Link to={`/builder?app=${generation.app_id}`}>打开设计器</Link>
                </Button>
              </div>
            )}
            {generation && generation.status !== "completed" && (
              <p className="text-sm text-destructive" data-testid="guide-generation-failed">
                生成失败：{generation.error_log ?? "未知错误"}
              </p>
            )}
            {generationError && <p className="text-sm text-destructive">{generationError}</p>}
          </div>
        </StepCard>

        {/* ④ 发布坊市 */}
        <StepCard step={STEPS[3].step} title={STEPS[3].title} description={STEPS[3].description} status={{ done: false, label: "待发布" }}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              在「应用管理」中找到已生成的应用，点击卡片上的「发布到坊市」：
              <code className="mx-1 rounded bg-muted px-1">POST /api/v1/debugpavilion/&#123;id&#125;/publish</code>
              （202 异步）→ Decross 审核：draft（审核中）→ published（已发布）/ rejected（已拒绝，可见原因）→ iframe 免登预览。
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild data-testid="guide-to-dashboard">
                <Link to="/dashboard">
                  <Store className="mr-1.5 h-4 w-4" />
                  前往应用管理
                </Link>
              </Button>
            </div>
          </div>
        </StepCard>
      </div>

      {/* 使用案例（对应 docs/使用案例.md 的 3 个真实联调案例） */}
      <div className="space-y-4">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold tracking-tight">
            <Rocket className="h-5 w-5 text-indigo-600" />
            使用案例
          </h2>
          <p className="text-sm text-muted-foreground">以下为已真实联调验证的端到端案例（详见 docs/使用案例.md）。</p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="flex flex-col">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">案例一 · 生成待办事项应用</CardTitle>
              </div>
              <CardDescription className="text-xs">真实 LLM 生成 → 发布坊市 → 审核 → 预览</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 space-y-2 text-xs text-muted-foreground">
              <p>需求 → SDD 5 阶段流水线 → 产物落库（package.json / 源码 / Schema）。</p>
              <p>「发布到坊市」→ pending → draft 审核中 → published；被拒可见 review_reason。</p>
              <p>published 后在 Decross 以 iframe 免登预览。</p>
            </CardContent>
          </Card>
          <Card className="flex flex-col">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">案例二 · Bureau 任务闭环</CardTitle>
              </div>
              <CardDescription className="text-xs">B 派发任务 → 生成 → 回调 → 任务 DONE</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 space-y-2 text-xs text-muted-foreground">
              <p>provision 注册「罢歌亭代码生成」团队 + 入职 7 Agent（幂等）。</p>
              <p>B 侧把 BUREAU_CELESTIAL_TOAD_BASE_URL 指向罢歌亭，POST /chat 派发（202）。</p>
              <p>SDD 生成后回调 done / failed，任务 DONE + Result 自动收集。</p>
            </CardContent>
          </Card>
          <Card className="flex flex-col">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-indigo-600" />
                <CardTitle className="text-base">案例三 · Facet 通知</CardTitle>
              </div>
              <CardDescription className="text-xs">控制台发通知 → 铃铛收到 → 已读</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 space-y-2 text-xs text-muted-foreground">
              <p>登录后顶部铃铛展示 Facet 通知：未读徽标 + 下拉列表，60s 轮询刷新。</p>
              <p>Facet 控制台发站内信 → 铃铛收到；单条已读 / 全部已读。</p>
              <p>生成流水线完成 / 失败自动推送通知，点击跳转设计器。</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
